import { z } from 'zod';
import { RSVP_STATUSES, TASK_CATEGORIES } from './constants.js';
import { isoDateSchema, shortText } from './common.js';

/**
 * A change the chat has worked out but not made.
 *
 * The whole safety model in one shape: a proposal names a concrete, already
 * resolved target — a real `taskId`, a real `guestId` — and carries no way to
 * apply itself. The couple confirms it, and confirming calls the ordinary
 * authenticated endpoint that any button on the page would call. There is no
 * second write path to audit.
 *
 * `summary` is what the person reads. It is written by the server from the
 * resolved row, not by the model, so it cannot describe a change other than the
 * one that would actually happen.
 */

export const addTaskProposalSchema = z.object({
  kind: z.literal('add_task'),
  summary: shortText,
  title: shortText,
  category: z.enum(TASK_CATEGORIES),
  dueDate: isoDateSchema.optional(),
});

export const completeTaskProposalSchema = z.object({
  kind: z.literal('complete_task'),
  summary: shortText,
  taskId: z.string().min(1).max(40),
  title: shortText,
});

export const setRsvpProposalSchema = z.object({
  kind: z.literal('set_rsvp'),
  summary: shortText,
  guestId: z.string().min(1).max(40),
  name: shortText,
  rsvp: z.enum(RSVP_STATUSES),
});

export const proposalSchema = z.discriminatedUnion('kind', [
  addTaskProposalSchema,
  completeTaskProposalSchema,
  setRsvpProposalSchema,
]);
export type Proposal = z.infer<typeof proposalSchema>;

/**
 * How many proposals one answer may carry.
 *
 * Small on purpose. A chat that returns nine things to confirm is a chat that
 * gets a blanket yes, which is the failure this whole design exists to prevent.
 */
export const MAX_PROPOSALS = 3;
